import React, { useCallback, useEffect, useRef, useState } from "react";
import UITable from "../../../../Domain/Components/UI/Table/Table";
import usePagination from "../../../../Infrastructure/Hooks/usePagination";
import UISearchBar from "../../../../Domain/Components/UI/SearchBar/SearchBar";
import PropTypes from "prop-types";

function GenericListPage(props) {
    const [sortState, setSortState] = useState({ sortBy: 'id', direction: 'desc' })
    const [searchValues, setSearchValues] = useState({});
    const [page, setPage] = useState(1)
    const [sortChangedOnFirstPage, setSortChangedOnFirstPage] = useState(0);
    const { data, more, loading, error } = usePagination(page, 100, props.readEndpoint, sortState, sortChangedOnFirstPage, props.setLoggedIn, props.relations ? props.relations : [], props.withoutIsActiveFilter ? false : true, searchValues);
    const [rows, setRows] = useState(data);

    useEffect(() => {
        setRows(data);
    }, [data])

    // Observer
    const observer = useRef(); // ref to store observer

    const lastElementRef = useCallback((element) => {
        //element is the react element being referenced

        // disconnect observer set on previous last element
        if (observer.current) observer.current.disconnect();

        // if there's no more data to be fetched, don't set new observer
        if (!more) return;

        // set new observer
        observer.current = new IntersectionObserver((entries) => {
            // increase page number when element enters (is intersecting with) viewport.
            // This triggers the pagination hook to fetch more items in the new page
            if (entries[0].isIntersecting && more) setPage((prev) => prev + 1);
        });

        // observe/monitor last element
        if (element) observer.current.observe(element);
    }, [more]);


    const sortingChanged = (columnKey) => {
        var tmpSortState = {
            sortBy: columnKey,
            direction: sortState.sortBy === columnKey ? sortState.direction === 'desc' ? 'asc' : 'desc' : 'desc'
        }

        setSortState(tmpSortState);
    }

    useEffect(() => {
        if (page === 1) {
            setSortChangedOnFirstPage(sortChangedOnFirstPage + 1);
        }
        setPage(1);
    }, [sortState, searchValues])

    const onSearch = (searchVal) => {
        var searchObj = {};
        props.objectKeys.forEach((key) => {
            if (!key.includes(".")) {
                searchObj[key] = searchVal;
            } else {
                if (!searchObj.relationsSearch) searchObj.relationsSearch = {};
                var keys = key.split(".");
                if (keys.length === 2) {
                    if (!searchObj.relationsSearch[keys[0]]) {
                        searchObj.relationsSearch[keys[0]] = {};
                    }
                    searchObj.relationsSearch[keys[0]][keys[1]] = searchVal;
                }
            }
        })
        setSearchValues(searchObj);
    }

    const onSearchInputChange = (e) => {
        const inputVal = e.target.value;
        if (!inputVal || inputVal === "") {
            setSearchValues({});
        }
    }

    return (
        <>
            {data &&
                <div className='container'>
                    <div className='d-flex justify-content-between m-4'>
                        <h3>{props.pageTitle}</h3>
                    </div>
                    <div className='d-flex justify-content-between ms-4 me-4 mb-2'>
                        <UISearchBar onSearch={(val) => onSearch(val)} onInputChange={onSearchInputChange}/>
                        <span></span>
                    </div>
                    <div className='ms-4 me-4'>
                        <UITable columnTitles={props.columnTitles}
                            data={data}
                            objectKeys={props.objectKeys}
                            primaryKey={'id'}
                            lastElementRef={lastElementRef}
                            detailsHref={props.detailsHref}
                            sortState={sortState}
                            sortingChanged={sortingChanged}
                            preventDelete={true}
                        ></UITable>
                        {loading & (more || page === 1) ? <div className="d-flex justify-content-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div> : <span></span>}
                        {!more && !loading && <p className='text-center p-4 text-secondary font-weight-bold'>No more data to be loaded</p>}
                    </div>
                </div>
            }
        </>
    )
}

GenericListPage.propTypes = {
    setLoggedIn: PropTypes.func,
    detailsHref: PropTypes.string,
    objectKeys: PropTypes.arrayOf(PropTypes.string),
    columnTitles: PropTypes.arrayOf(PropTypes.string),
    pageTitle: PropTypes.string,
    readEndpoint: PropTypes.string,
    relations: PropTypes.arrayOf(PropTypes.string),
    withoutIsActiveFilter: PropTypes.bool
}

export default GenericListPage;